import { useCallback, useEffect, useState } from "react";
import QRCode from "qrcode";
import { toast } from "sonner";
import { CheckCircle2, Loader2, QrCode, Banknote, RefreshCw, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getConsultPayQuote, startConsultPay, getConsultPay, cancelConsultPay } from "@/lib/api";

// Take the first payment at the end of a consult (routes/consult_pay.py). The rep sets the price it
// sold at and how much is paid today; the lead pays on this screen (QR, 10 minutes) or in cash.
// There's no "send link": the payment happens here, before they leave.

const TYPES = [
  ["full", "Full payment"],
  ["part", "Part payment"],
  ["deposit", "Upfront deposit (refundable)"],
];
const money = (sym, n) => `${sym}${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const err = (e, fallback) => e?.response?.data?.detail || fallback;

function Countdown({ until, onEnd }) {
  const [left, setLeft] = useState(() => Math.max(0, new Date(until) - Date.now()));
  useEffect(() => {
    const t = setInterval(() => {
      const ms = Math.max(0, new Date(until) - Date.now());
      setLeft(ms);
      if (!ms) { clearInterval(t); onEnd?.(); }
    }, 1000);
    return () => clearInterval(t);
  }, [until, onEnd]);
  const m = Math.floor(left / 60000), s = Math.floor((left % 60000) / 1000);
  return <span className={`font-mono ${left < 60000 ? "text-rose-300" : "text-zinc-300"}`}>{m}:{String(s).padStart(2, "0")}</span>;
}

function Qr({ value, size = 280 }) {
  const [png, setPng] = useState(null);
  useEffect(() => { if (value) QRCode.toDataURL(value, { width: size * 2, margin: 2 }).then(setPng).catch(() => setPng(null)); }, [value, size]);
  return png ? <img src={png} alt="Scan to pay" width={size} height={size} className="rounded-xl bg-white p-2" /> : <div style={{ width: size, height: size }} />;
}

export default function TakePayment({ lead, open, onClose, onPaid }) {
  const [q, setQ] = useState(null);
  const [programme, setProgramme] = useState("");
  const [price, setPrice] = useState("");
  const [type, setType] = useState("full");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [pay, setPay] = useState(null);           // the payment in progress (or done)

  const loadQuote = useCallback(async (prog) => {
    try {
      const r = await getConsultPayQuote(lead.id, prog || undefined);
      setQ(r);
      if (!prog) setProgramme(r.programme || "");
      setPrice(r.price != null ? String(r.price) : "");
      if (r.open && !pay) setPay(r.open);
    } catch (e) { toast.error(err(e, "Couldn't load the price")); }
  }, [lead?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (open && lead) { setPay(null); setType("full"); setAmount(""); loadQuote(); } }, [open, lead]); // eslint-disable-line react-hooks/exhaustive-deps

  // While the QR is up, check every 3 s whether it's been paid.
  useEffect(() => {
    if (!pay || pay.status !== "pending") return undefined;
    const t = setInterval(async () => {
      try {
        const r = await getConsultPay(pay.id);
        setPay(r.payment);
        if (r.payment.status === "paid") onPaid?.();
      } catch { /* keep trying */ }
    }, 3000);
    return () => clearInterval(t);
  }, [pay, onPaid]);

  if (!lead) return null;
  const sym = q?.currency_symbol || "₹";
  const rate = q?.tax_rate || 0;
  const sold = parseFloat(price) || 0;
  const total = Math.round(sold * (1 + rate) * 100) / 100;
  const now = type === "full" ? total : parseFloat(amount) || 0;
  const diff = q?.price != null && sold ? sold - q.price : 0;

  const go = async (method) => {
    if (!programme) return toast.error("Pick the programme");
    if (!sold) return toast.error("Enter the price it was sold at");
    if (type !== "full" && !(now > 0)) return toast.error("Enter how much they're paying now");
    if (type !== "full" && now >= total) return toast.error("That's the full amount. Choose Full payment.");
    setBusy(true);
    try {
      const r = await startConsultPay(lead.id, { programme, sold_price: sold, pay_type: type, amount: type === "full" ? undefined : now, method });
      setPay(r.payment);
      if (r.payment.status === "paid") onPaid?.();
    } catch (e) { toast.error(err(e, "Couldn't start the payment")); }
    finally { setBusy(false); }
  };
  const stop = async () => {
    if (pay?.status === "pending") { try { setPay((await cancelConsultPay(pay.id)).payment); } catch { /* ignore */ } }
    setPay(null);
  };

  const paid = pay && pay.status === "paid";
  const expired = pay && ["expired", "cancelled", "replaced", "failed"].includes(pay.status);
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-2xl bg-[#0b0b0b] border-[#1f1f1f] text-zinc-100" data-testid="take-payment">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Take payment · {lead.name}</DialogTitle>
          <DialogDescription className="text-zinc-400">
            They pay now, on this screen. The payment page works for {q?.minutes || 10} minutes.
          </DialogDescription>
        </DialogHeader>

        {q && !q.enabled && (
          <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200">
            Taking consult payments in FPA isn&apos;t switched on for this gym yet.
          </div>
        )}

        {q?.enabled && !pay && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="space-y-1">
                <span className="text-xs text-zinc-400">Programme</span>
                <select value={programme} onChange={(e) => { setProgramme(e.target.value); loadQuote(e.target.value); }}
                  className="h-11 w-full rounded-md border border-[#262626] bg-[#0a0a0a] px-3 text-zinc-100" data-testid="pay-programme">
                  <option value="">Pick…</option>
                  {(q.programmes || []).map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </label>
              <label className="space-y-1">
                <span className="text-xs text-zinc-400">Price sold at ({sym}, before {q.tax_name || "tax"})</span>
                <Input type="number" min="0" value={price} onChange={(e) => setPrice(e.target.value)} className="h-11 font-mono" data-testid="pay-price" />
                <span className="block text-[11px] text-zinc-500">
                  {q.price != null ? <>Catalog {money(sym, q.price)}{q.finisher ? " (6WC finisher price)" : ""}</> : "Not in the catalog"}
                  {diff ? <span className={diff < 0 ? "text-amber-300" : "text-emerald-300"}> · {diff < 0 ? "−" : "+"}{money(sym, Math.abs(diff))} ({diff < 0 ? "−" : "+"}{Math.abs(Math.round((diff / q.price) * 100))}%) vs catalog</span> : null}
                </span>
              </label>
            </div>

            <div className="space-y-2">
              <div className="text-xs text-zinc-400">How are they paying today?</div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                {TYPES.map(([k, label]) => (
                  <button key={k} type="button" onClick={() => setType(k)} data-testid={`pay-type-${k}`}
                    className={`rounded-lg border px-3 py-3 text-left text-sm ${type === k ? "border-blue-500 bg-blue-500/10 text-zinc-100" : "border-[#262626] text-zinc-300 hover:border-zinc-500"}`}>
                    {label}
                  </button>
                ))}
              </div>
              {type !== "full" && (
                <label className="block space-y-1">
                  <span className="text-xs text-zinc-400">Paying now ({sym}, incl. {q.tax_name || "tax"})</span>
                  <Input type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-11 font-mono" data-testid="pay-amount" />
                </label>
              )}
            </div>

            <div className="rounded-lg border border-[#262626] p-3 text-sm">
              <div className="flex justify-between"><span className="text-zinc-400">Total{q.tax_name ? ` incl. ${q.tax_name}` : ""}</span><span className="font-mono">{money(sym, total)}</span></div>
              <div className="flex justify-between text-base font-semibold"><span>Paying now</span><span className="font-mono">{money(sym, now)}</span></div>
              {type !== "full" && now > 0 && now < total && (
                <div className="flex justify-between text-amber-300"><span>Balance before they start</span><span className="font-mono">{money(sym, total - now)}</span></div>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              {q.methods.includes("online") && (
                <Button onClick={() => go("online")} disabled={busy} className="h-12 flex-1 bg-blue-600 text-base hover:bg-blue-500" data-testid="pay-online">
                  {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <QrCode className="mr-2 h-4 w-4" />} Show payment QR ({q.gateway})
                </Button>
              )}
              <Button variant="outline" onClick={() => { if (window.confirm(`Record ${money(sym, now)} received in cash by you?`)) go("cash"); }}
                disabled={busy} className="h-12" data-testid="pay-cash">
                <Banknote className="mr-2 h-4 w-4" /> Paid in cash
              </Button>
            </div>
          </div>
        )}

        {pay && pay.status === "pending" && (
          <div className="flex flex-col items-center gap-3 py-2" data-testid="pay-qr">
            <div className="text-lg font-semibold">{money(pay.currency_symbol, pay.amount)} · {pay.pay_type_label}</div>
            <Qr value={pay.checkout_url} />
            <div className="text-sm text-zinc-400">Scan with any UPI app or the phone camera. Closes in <Countdown until={pay.expires_at} onEnd={async () => { try { setPay((await getConsultPay(pay.id)).payment); } catch { /* ignore */ } }} /></div>
            <div className="flex items-center gap-2 text-xs text-zinc-500"><Loader2 className="h-3 w-3 animate-spin" /> Waiting for the payment…</div>
            <Button variant="ghost" onClick={stop} className="text-zinc-400"><X className="mr-1 h-4 w-4" /> Cancel this payment</Button>
          </div>
        )}

        {expired && (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <div className="text-lg">This payment page has closed.</div>
            <Button onClick={() => setPay(null)} data-testid="pay-again"><RefreshCw className="mr-2 h-4 w-4" /> Start again</Button>
          </div>
        )}

        {paid && (
          <div className="flex flex-col items-center gap-3 py-2 text-center" data-testid="pay-done">
            <CheckCircle2 className="h-14 w-14 text-emerald-400" />
            <div className="text-xl font-semibold">Paid: {money(pay.currency_symbol, pay.amount)}</div>
            <div className="text-sm text-zinc-400">
              {pay.balance > 0 ? `Balance ${money(pay.currency_symbol, pay.balance)} before they start. ` : ""}
              Saved on the consult; the bill is emailed shortly. Now get them on the LIFTR app:
            </div>
            <div className="flex flex-wrap justify-center gap-6">
              {q?.app_links?.ios_url && <div className="space-y-1"><Qr value={q.app_links.ios_url} size={150} /><div className="text-xs text-zinc-400">iPhone</div></div>}
              {q?.app_links?.android_url && <div className="space-y-1"><Qr value={q.app_links.android_url} size={150} /><div className="text-xs text-zinc-400">Android</div></div>}
            </div>
            <div className="text-xs text-zinc-500">They sign in with this phone number: {lead.phone}</div>
            <Button onClick={onClose} className="mt-2">Done</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
